import keyBy from 'lodash/keyBy';
import type { IssueTypeFieldValue } from '@jira/polaris__domain-field/src/IssueTypeFieldValue.types.tsx';
import type { FieldKey } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
// eslint-disable-next-line lodash/import-scope
import type { Dictionary } from 'lodash';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createStateHook,
	createActionsHook,
	createSelector,
	type BoundActions,
	type HookActionsFunction,
	type HookStateFunction,
	type StoreActionApi,
	type Store,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import { makeAvatarUrlFromId } from './makeAvatarUrlFromId.tsx';
import { actions } from './componentIssueTypesActions.tsx';
import type {
	IssueType,
	IssueTypeModifiableProps,
	IssueTypeWithPinnedFields,
	State,
} from './IssueType.types.tsx';

type IssueTypeId = string;
type ProjectId = string;

const EMPTY: IssueTypeId[] = [];

const initialState: State = {
	issueTypesById: {},
	issueTypesByProjectId: {},
};

export const IssueTypesStore: Store<State, typeof actions> = createStore({
	initialState,
	actions,
	name: 'PolarisIssueTypesStore',
});

export const useIssueTypesActions: HookActionsFunction<
	BoundActions<
		State,
		{
			fetchIssueTypesForProjects: (
				projectIds: string[],
			) => ({ setState, getState }: StoreActionApi<State>) => Promise<void>;
			fetchPinnedFieldsForType: ({
				issueTypeId,
				projectId,
			}: {
				issueTypeId: string;
				projectId: string;
			}) => ({ setState, getState }: StoreActionApi<State>) => Promise<void>;
			updateIssueType: (
				issueTypeId: string,
				projectId: string,
				update: IssueTypeModifiableProps & {
					avatarUrl?: string;
				},
			) => ({ setState, getState }: StoreActionApi<State>) => Promise<void>;
			createIssueType: (
				projectId: string,
				data: IssueTypeModifiableProps,
			) => ({
				setState,
				getState,
				dispatch,
			}: StoreActionApi<State>) => Promise<IssueTypeWithPinnedFields>;
			deleteIssueType: (
				issueTypeId: string,
				projectId: string,
			) => ({ dispatch }: StoreActionApi<State>) => Promise<void>;
			populateIssueTypes: ({
				issueTypes,
			}: {
				issueTypes: (IssueTypeModifiableProps & {
					id: string;
					entityId: string;
					hierarchyLevel: number;
					avatarUrl: string;
					operation: {
						deletable: boolean;
						editable: boolean;
					};
				} & {
					pinnedFields?: FieldKey[];
				} & {
					projectId: string;
				})[];
			}) => ({ setState, getState }: StoreActionApi<State>) => Promise<void>;
			togglePinnedField: ({
				issueTypeId,
				fieldKey,
			}: {
				issueTypeId: string;
				fieldKey: FieldKey;
			}) => ({ getState, dispatch }: StoreActionApi<State>) => Promise<void>;
			updatePinnedFields: ({
				issueTypeId,
				pinnedFields,
			}: {
				issueTypeId: string;
				pinnedFields: FieldKey[];
			}) => ({ setState, getState }: StoreActionApi<State>) => Promise<void>;
			clearIssueTypesStore: () => ({ setState }: StoreActionApi<State>) => void;
		}
	>
> = createActionsHook(IssueTypesStore);

const getAllIssueTypesById = (state: State) => state.issueTypesById;
const getAllIssueTypeIds = (state: State) => Object.keys(state.issueTypesById);
const getAllIssueTypeIdsByProjectId = (state: State) => state.issueTypesByProjectId;

const getAllIssueTypesByName = createSelector(
	getAllIssueTypesById,
	(issueTypesById): Record<string, IssueTypeWithPinnedFields> =>
		keyBy(Object.values(issueTypesById).filter(Boolean), 'name'),
);

export const useAllIssueTypesByName: HookStateFunction<
	Record<string, IssueTypeWithPinnedFields>,
	void
> = createStateHook(IssueTypesStore, {
	selector: getAllIssueTypesByName,
});

export const useAllIssueTypesById: HookStateFunction<
	Record<string, IssueTypeWithPinnedFields | undefined>,
	void
> = createStateHook(IssueTypesStore, {
	selector: getAllIssueTypesById,
});

const getProjectIds = createSelector(getAllIssueTypeIdsByProjectId, (issueTypesByProjectId) =>
	Object.keys(issueTypesByProjectId),
);

export const useIssueTypesProjectIds: HookStateFunction<string[], void> = createStateHook(
	IssueTypesStore,
	{
		selector: getProjectIds,
	},
);

export const useAllIssueTypeIds: HookStateFunction<string[], void> = createStateHook(
	IssueTypesStore,
	{
		selector: getAllIssueTypeIds,
	},
);

export const useAllIssueTypeIdsByProjectId: HookStateFunction<
	Record<string, string[] | undefined>,
	void
> = createStateHook(IssueTypesStore, {
	selector: getAllIssueTypeIdsByProjectId,
});

const getAllIssueTypesByProjectId = createSelector(
	getAllIssueTypeIdsByProjectId,
	getAllIssueTypesById,
	(issueTypesByProjectId, issueTypesById) => {
		return Object.keys(issueTypesByProjectId).reduce<Record<string, IssueType[] | undefined>>(
			(acc, projectId) => {
				acc[projectId] = issueTypesByProjectId[projectId]
					?.map((issueTypeId) => issueTypesById[issueTypeId])
					.filter(Boolean);
				return acc;
			},
			{},
		);
	},
);

export const useAllIssueTypesByProjectId: HookStateFunction<
	Record<string, IssueType[] | undefined>,
	void
> = createStateHook(IssueTypesStore, {
	selector: getAllIssueTypesByProjectId,
});

const getIssueTypeIdsForProject = (
	state: State,
	{ projectId }: { projectId: ProjectId | undefined },
): IssueTypeId[] => {
	if (projectId === undefined) {
		return EMPTY;
	}
	return state.issueTypesByProjectId[projectId] || EMPTY;
};

const getIssueTypeIdsForProjectIds = (
	state: State,
	{ projectIds }: { projectIds: ProjectId[] },
): IssueTypeId[] => {
	return projectIds.flatMap((projectId) => state.issueTypesByProjectId[projectId] || EMPTY);
};

export const useIssueTypeIdsForProject: HookStateFunction<
	string[],
	{
		projectId: ProjectId | undefined;
	}
> = createStateHook(IssueTypesStore, {
	selector: getIssueTypeIdsForProject,
});

const getIssueTypesForProject = createSelector(
	getAllIssueTypesById,
	getIssueTypeIdsForProject,
	(issueTypesById, issueTypeIds) =>
		issueTypeIds.map((issueTypeId) => issueTypesById[issueTypeId]).filter(Boolean),
);

export const useIssueTypesForProject: HookStateFunction<
	IssueTypeWithPinnedFields[],
	{
		projectId: ProjectId | undefined;
	}
> = createStateHook(IssueTypesStore, {
	selector: getIssueTypesForProject,
});

const getIssueTypesForProjectIds = createSelector(
	getAllIssueTypesById,
	getIssueTypeIdsForProjectIds,
	(issueTypesById, issueTypeIds) =>
		issueTypeIds.map((issueTypeId) => issueTypesById[issueTypeId]).filter(Boolean),
);

export const useIssueTypesForProjectIds: HookStateFunction<
	IssueTypeWithPinnedFields[],
	{
		projectIds: ProjectId[];
	}
> = createStateHook(IssueTypesStore, {
	selector: getIssueTypesForProjectIds,
});

const getIssueTypesByNameForProjectIds = createSelector(getIssueTypesForProjectIds, (issueTypes) =>
	keyBy(issueTypes, 'name'),
);

export const useIssueTypesByNameForProjectIds: HookStateFunction<
	Dictionary<IssueTypeWithPinnedFields>,
	{
		projectIds: ProjectId[];
	}
> = createStateHook(IssueTypesStore, {
	selector: getIssueTypesByNameForProjectIds,
});

/**
 * @deprecated Use useIssueTypeIdsForProject instead, we can no longer
 * guarantee a single issue type per project
 */
export const useIssueTypeIdForProject = ({
	projectId,
}: {
	projectId: ProjectId | undefined;
}): IssueTypeId | undefined => {
	const issueTypeIds = useIssueTypeIdsForProject({ projectId });
	return issueTypeIds.length > 0 ? issueTypeIds[0] : undefined;
};

export const useIssueTypeName: HookStateFunction<
	string | undefined,
	{
		issueTypeId: IssueTypeId;
	}
> = createStateHook(IssueTypesStore, {
	selector: (state, { issueTypeId }: { issueTypeId: IssueTypeId }) =>
		state.issueTypesById[issueTypeId]?.name,
});

export const useIssueTypeDescription: HookStateFunction<
	string | undefined,
	{
		issueTypeId: IssueTypeId;
	}
> = createStateHook(IssueTypesStore, {
	selector: (state, { issueTypeId }: { issueTypeId: IssueTypeId }) =>
		state.issueTypesById[issueTypeId]?.description,
});

export const useIssueTypeAvatarId: HookStateFunction<
	string | undefined,
	{
		issueTypeId: IssueTypeId;
	}
> = createStateHook(IssueTypesStore, {
	selector: (state, { issueTypeId }: { issueTypeId: IssueTypeId }) =>
		state.issueTypesById[issueTypeId]?.avatarId,
});

export const useIssueTypeAvatarUrl: HookStateFunction<
	string | undefined,
	{
		issueTypeId: IssueTypeId;
	}
> = createStateHook(IssueTypesStore, {
	selector: (state, { issueTypeId }: { issueTypeId: IssueTypeId }) =>
		state.issueTypesById[issueTypeId]?.avatarUrl,
});

export const useIsIssueTypeDeletable: HookStateFunction<
	boolean,
	{
		issueTypeId: IssueTypeId;
	}
> = createStateHook(IssueTypesStore, {
	selector: (state, { issueTypeId }: { issueTypeId: IssueTypeId }) =>
		state.issueTypesById[issueTypeId]?.operation.deletable === true,
});

export const useIsIssueTypeEditable: HookStateFunction<
	boolean,
	{
		issueTypeId: IssueTypeId;
	}
> = createStateHook(IssueTypesStore, {
	selector: (state, { issueTypeId }: { issueTypeId: IssueTypeId }) =>
		state.issueTypesById[issueTypeId]?.operation.editable === true,
});

export const useIssueTypeProjectId: HookStateFunction<
	string | undefined,
	{
		issueTypeId: IssueTypeId;
	}
> = createStateHook(IssueTypesStore, {
	selector: (state, { issueTypeId }: { issueTypeId: IssueTypeId }) =>
		Object.keys(state.issueTypesByProjectId).find((projectId) =>
			state.issueTypesByProjectId[projectId]?.includes(issueTypeId),
		),
});

const transformToFieldValue = ({
	id,
	name,
	avatarId,
	avatarUrl,
}: IssueType): IssueTypeFieldValue => ({
	id,
	name,
	iconUrl: fg('jpd_new-issue-types-avatars') ? avatarUrl : makeAvatarUrlFromId(avatarId),
});

const getIssueTypeValuesForProject = createSelector(
	getIssueTypesForProject,
	(issueTypes): IssueTypeFieldValue[] => issueTypes.map(transformToFieldValue),
);

export const useIssueTypeValuesForProject: HookStateFunction<
	IssueTypeFieldValue[],
	{
		projectId: ProjectId | undefined;
	}
> = createStateHook(IssueTypesStore, {
	selector: getIssueTypeValuesForProject,
});

const getIssueTypeValue = (
	state: State,
	{ issueTypeId }: { issueTypeId: IssueTypeId | undefined },
) => {
	if (!issueTypeId) {
		return undefined;
	}

	const issueType = state.issueTypesById[issueTypeId];

	if (!issueType) {
		return undefined;
	}

	return transformToFieldValue(issueType);
};

export const useIssueTypeValue: HookStateFunction<
	IssueTypeFieldValue | undefined,
	{
		issueTypeId: IssueTypeId | undefined;
	}
> = createStateHook(IssueTypesStore, {
	selector: getIssueTypeValue,
});

const getIssueTypeValuesForProjectIds = createSelector(
	getIssueTypesForProjectIds,
	(issueTypes): IssueTypeFieldValue[] => issueTypes.map(transformToFieldValue),
);

export const useIssueTypeValuesForProjectIds: HookStateFunction<
	IssueTypeFieldValue[],
	{
		projectIds: ProjectId[];
	}
> = createStateHook(IssueTypesStore, {
	selector: getIssueTypeValuesForProjectIds,
});

export const getIssueTypeValuesPrioritizingProjectIds: (
	state: State,
	{
		projectIds,
	}: {
		projectIds: ProjectId[];
	},
) => IssueTypeFieldValue[] = createSelector(
	getIssueTypesForProjectIds,
	getAllIssueTypesById,
	(projectIssueTypes, allIssueTypesById): IssueTypeFieldValue[] => {
		const prioritizedIssueTypes = [...projectIssueTypes, ...Object.values(allIssueTypesById)];
		const issueTypesByName = new Map<string, IssueTypeFieldValue>();

		for (const issueType of prioritizedIssueTypes) {
			if (issueType && !issueTypesByName.has(issueType.name)) {
				issueTypesByName.set(issueType.name, transformToFieldValue(issueType));
			}
		}

		return Array.from(issueTypesByName.values());
	},
);

export const useIssueTypeValuesPrioritizingProjectIds: HookStateFunction<
	IssueTypeFieldValue[],
	{
		projectIds: ProjectId[];
	}
> = createStateHook(IssueTypesStore, {
	selector: getIssueTypeValuesPrioritizingProjectIds,
});

const getAllIssueTypeValuesById = createSelector(getAllIssueTypesById, (allIssueTypes) => {
	const issueTypeMap = new Map<string, IssueTypeFieldValue>();
	Object.values(allIssueTypes).forEach((issueType) => {
		issueType && issueTypeMap.set(issueType.id, transformToFieldValue(issueType));
	});
	return issueTypeMap;
});

export const useAllIssueTypeValuesById: HookStateFunction<
	Map<string, IssueTypeFieldValue>,
	void
> = createStateHook(IssueTypesStore, {
	selector: getAllIssueTypeValuesById,
});

const hasProjectCustomIssueTypes = createSelector(
	getIssueTypeIdsForProject,
	(issueTypeIds) => issueTypeIds.length > 1,
);

export const useHasProjectCustomIssueTypes: HookStateFunction<
	boolean,
	{
		projectId: ProjectId | undefined;
	}
> = createStateHook(IssueTypesStore, {
	selector: hasProjectCustomIssueTypes,
});

const getIssueTypeByNameAndProjectIds = (
	state: State,
	{ name, projectIds }: { name?: string; projectIds?: ProjectId[] },
) => {
	if (!name) {
		return;
	}

	if (!projectIds?.length) {
		return Object.values(state.issueTypesById).find((issueType) => issueType?.name === name);
	}

	for (const projectId of projectIds) {
		const issueTypeId = state.issueTypesByProjectId[projectId]?.find(
			(id) => state.issueTypesById[id]?.name === name,
		);

		if (issueTypeId) {
			return state.issueTypesById[issueTypeId];
		}
	}
};

export const useIssueTypeByNameAndProjectIds: HookStateFunction<
	IssueTypeWithPinnedFields | undefined,
	{
		name?: string;
		projectIds?: ProjectId[];
	}
> = createStateHook(IssueTypesStore, {
	selector: getIssueTypeByNameAndProjectIds,
});

export const useValidTransitionIssueTypeTargetNames = ({
	projectId,
}: {
	projectId?: string;
}): string[] => {
	const allIssueTypeIdsByProjectId = useAllIssueTypeIdsByProjectId();
	const allIssueTypesById = useAllIssueTypesById();

	if (!projectId) {
		return [];
	}

	return (
		allIssueTypeIdsByProjectId[projectId]
			?.map((issueTypeId) => allIssueTypesById[issueTypeId]?.name)
			.filter(Boolean) ?? []
	);
};

const getIssueTypePinnedFieldKeys = (state: State, { issueTypeId }: { issueTypeId: IssueTypeId }) =>
	state.issueTypesById[issueTypeId]?.pinnedFields || [];

export const useIssueTypePinnedFieldKeys: HookStateFunction<
	string[],
	{
		issueTypeId: IssueTypeId;
	}
> = createStateHook(IssueTypesStore, {
	selector: getIssueTypePinnedFieldKeys,
});

const getAllPinnedFieldKeys = (state: State) =>
	Object.values(state.issueTypesById).flatMap((issueType) => issueType?.pinnedFields || []);

export const useAllPinnedFieldKeys: HookStateFunction<string[], void> = createStateHook(
	IssueTypesStore,
	{
		selector: getAllPinnedFieldKeys,
	},
);
